import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, FileText, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { profileData } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const purposeOptions = ["Business Consultation", "HR Strategy Discussion", "Partnership Opportunity", "Career Advice", "Speaking Engagement", "Other"];

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', purpose: '', notes: '' });

  useEffect(() => { if (selectedDate) { fetchAvailableSlots(selectedDate); } }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true); setSelectedSlot(''); setError('');
    try {
      const formattedDate = date.toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/bookings/available-slots?date=${formattedDate}`);
      setAvailableSlots(response.data.slots || []);
      if (response.data.message) { setError(response.data.message); }
    } catch (err) { setError('Failed to fetch available slots'); setAvailableSlots([]); }
    finally { setSlotsLoading(false); }
  };

  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const bookingData = { ...formData, date: selectedDate.toISOString().split('T')[0], time_slot: selectedSlot };
      await axios.post(`${API_URL}/api/bookings`, bookingData);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create booking. Please try again.'); }
    finally { setLoading(false); }
  };

  const isWeekday = (date) => { const day = date.getDay(); return day !== 0 && day !== 6; };
  const isPastDate = (date) => { const today = new Date(); today.setHours(0, 0, 0, 0); return date < today; };
  const isFormValid = selectedDate && selectedSlot && formData.name && formData.email && formData.phone && formData.purpose;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#faf6ee]">
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#faf6ee]/95 backdrop-blur-md shadow-[0_1px_0_rgba(201,168,76,0.15)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <Link to="/" className="flex items-center gap-2"><img src="/sn-logo.png" alt="SN Logo" className="h-20 w-auto" /></Link>
            </div>
          </div>
        </header>
        <main className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-[#fffdf8] rounded-2xl shadow-xl p-12 border border-[#e6dfd0]">
              <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-green-400" strokeWidth={2.5} /></div>
              <h1 className="text-3xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="booking-confirmed-title">Booking Confirmed!</h1>
              <p className="text-[#5c6878] mb-8">Your appointment with {profileData.name} has been successfully scheduled.</p>
              <div className="bg-[#faf6ee] rounded-xl p-6 text-left mb-8 border border-[#e6dfd0]">
                <h3 className="font-semibold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Appointment Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-[#5c6878]"><CalendarIcon className="w-5 h-5 text-gold-400" strokeWidth={2.5} /><span>{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                  <div className="flex items-center gap-3 text-[#5c6878]"><Clock className="w-5 h-5 text-gold-400" strokeWidth={2.5} /><span>{selectedSlot} (UAE Time)</span></div>
                  <div className="flex items-center gap-3 text-[#5c6878]"><FileText className="w-5 h-5 text-gold-400" strokeWidth={2.5} /><span>{formData.purpose}</span></div>
                </div>
              </div>
              <p className="text-sm text-[#8b94a3] mb-8">A confirmation email will be sent to {formData.email}</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-all duration-300" data-testid="back-to-home-btn"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />Back to Homepage</Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf6ee]" data-testid="booking-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#faf6ee]/95 backdrop-blur-md shadow-[0_1px_0_rgba(201,168,76,0.15)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2"><img src="/sn-logo.png" alt="SN Logo" className="h-20 w-auto" /></Link>
            <Link to="/" className="flex items-center gap-2 text-[#5c6878] hover:text-gold-400 transition-colors"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />Back</Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Book an <span className="text-gold-400">Appointment</span></h1>
            <p className="text-lg text-[#5c6878] max-w-2xl mx-auto">Schedule a meeting with {profileData.name}. Available Monday to Friday, 9:00 AM - 6:00 PM (UAE Time).</p>
          </div>

          <div className="bg-[#fffdf8] rounded-2xl shadow-xl border border-[#e6dfd0] overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-8 border-b lg:border-b-0 lg:border-r border-[#e6dfd0]">
                <h2 className="text-xl font-semibold text-[#152846] mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}><CalendarIcon className="w-5 h-5 text-gold-400" strokeWidth={2.5} />Select Date</h2>
                <div className="flex justify-center">
                  <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={(date) => isPastDate(date) || !isWeekday(date)} className="rounded-lg border border-[#e6dfd0] bg-[#faf6ee] text-[#152846]" data-testid="booking-calendar" />
                </div>
                {selectedDate && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-[#152846] mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}><Clock className="w-5 h-5 text-gold-400" strokeWidth={2.5} />Available Times</h3>
                    {slotsLoading ? (
                      <div className="text-center py-8 text-[#8b94a3]">Loading available slots...</div>
                    ) : error && availableSlots.length === 0 ? (
                      <div className="text-center py-8 text-gold-400 bg-gold-400/5 rounded-lg border border-gold-400/20">{error}</div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                        {availableSlots.map((slot) => (
                          <button key={slot.time} onClick={() => slot.available && setSelectedSlot(slot.time)} disabled={!slot.available}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${selectedSlot === slot.time ? 'bg-gold-400 text-black shadow-md shadow-gold-400/20' : slot.available ? 'bg-[#faf6ee] text-[#3e4858] border border-[#e6dfd0] hover:border-gold-400/30' : 'bg-[#faf6ee] text-[#aab2bf] cursor-not-allowed line-through border border-[#e6dfd0]'}`}
                            data-testid={`time-slot-${slot.time.replace(/[:\s]/g, '-')}`}
                          >{slot.time}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-8">
                <h2 className="text-xl font-semibold text-[#152846] mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}><User className="w-5 h-5 text-gold-400" strokeWidth={2.5} />Your Details</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
                  {[{ name: 'name', icon: User, label: 'Full Name', type: 'text', placeholder: 'John Doe' }, { name: 'email', icon: Mail, label: 'Email Address', type: 'email', placeholder: 'john@example.com' }, { name: 'phone', icon: Phone, label: 'Phone Number', type: 'tel', placeholder: '+971 50 123 4567' }].map(({ name, icon: Icon, label, type, placeholder }) => (
                    <div key={name}>
                      <label className="block text-sm font-medium text-[#5c6878] mb-2">{label} *</label>
                      <div className="relative">
                        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#aab2bf]" strokeWidth={2.5} />
                        <input type={type} name={name} value={formData[name]} onChange={handleInputChange} required placeholder={placeholder}
                          className="w-full pl-10 pr-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] text-[#152846] rounded-lg focus:ring-2 focus:ring-gold-400 focus:border-gold-400 transition-all placeholder-[#aab2bf]"
                          data-testid={`booking-${name}-input`}
                        />
                      </div>
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-[#5c6878] mb-2">Purpose of Meeting *</label>
                    <Select onValueChange={(value) => setFormData(prev => ({ ...prev, purpose: value }))}>
                      <SelectTrigger className="w-full py-3 bg-[#faf6ee] border-[#e6dfd0] text-[#152846]" data-testid="booking-purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                      <SelectContent className="bg-[#fffdf8] border-[#e6dfd0] text-[#152846]">
                        {purposeOptions.map((option) => (<SelectItem key={option} value={option} className="text-[#3e4858] hover:text-gold-400 focus:text-gold-400 focus:bg-gold-400/10">{option}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5c6878] mb-2">Additional Notes (Optional)</label>
                    <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any specific topics you'd like to discuss..."
                      className="w-full px-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] text-[#152846] rounded-lg focus:ring-2 focus:ring-gold-400 focus:border-gold-400 transition-all resize-none placeholder-[#aab2bf]"
                      data-testid="booking-notes-input"
                    />
                  </div>
                  {error && <div className="p-4 bg-red-900/20 border border-red-800/30 rounded-lg text-red-400 text-sm" data-testid="booking-error">{error}</div>}
                  <button type="submit" disabled={!isFormValid || loading}
                    className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 ${isFormValid && !loading ? 'bg-gold-400 text-black hover:bg-gold-300 shadow-lg shadow-gold-400/20' : 'bg-[#e6dfd0] text-[#aab2bf] cursor-not-allowed'}`}
                    data-testid="booking-submit-btn"
                  >
                    {loading ? (<span className="flex items-center justify-center gap-2"><span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />Booking...</span>) : 'Confirm Booking'}
                  </button>
                  {!selectedDate && <p className="text-center text-sm text-[#8b94a3]">Please select a date to see available time slots</p>}
                </form>
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[{ icon: CalendarIcon, title: 'Flexible Scheduling', desc: 'Choose from available slots Monday through Friday, 9 AM to 6 PM UAE time.' },
              { icon: Clock, title: '1-Hour Sessions', desc: 'Each appointment is scheduled for one hour to ensure quality discussion time.' },
              { icon: Mail, title: 'Confirmation Email', desc: "You'll receive a confirmation email with meeting details after booking." }
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="bg-[#fffdf8] rounded-xl p-6 border border-[#e6dfd0]" data-testid={`booking-info-${i}`}>
                <div className="w-12 h-12 bg-gold-400/10 rounded-lg flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-gold-400" strokeWidth={2.5} /></div>
                <h3 className="font-semibold text-[#152846] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
                <p className="text-[#5c6878] text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Booking;
